/**
 * useSubscriptionHistory — Stream S (Paritaet R4)
 *
 * GET /api/v2/subscriptions/history liefert die VEREINHEITLICHTE Premium-Historie:
 * Stripe-Abos (alle Stati), Krypto-/Einmalzahlungen und offene Upgrade-Codes.
 * Nutzer ohne Payment-Processor bekommen 200 mit leerer Liste (kein 404) —
 * die Sektion zeigt dann den Leerzustand.
 *
 * ID-Semantik (Verwechslungsgefahr, deshalb strikt getrennt):
 *   - `id`              → zusammengesetzter React-Key ("stripe:42" | "crypto:7" | "code:3")
 *   - `subscription_id` → Pay-Datenbank-ID, NUR fuer source='stripe'; der Wert fuer
 *                         DELETE /api/v2/subscriptions/{id}
 *   - `stripe_id`       → volle "sub_..."-ID, reine Anzeige (gekuerzt + Copy)
 *
 * DELETE /api/v2/subscriptions/{id} kuendigt zum PERIODENENDE (kein cancel_now)
 * und ist bewusst NICHT hinter V2_PAYMENT_WRITES_ENABLED. Das 501-Mapping bleibt
 * trotzdem erhalten, damit ein spaeteres Gating im FE nicht als generischer
 * Fehler erscheint.
 */
import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { ApiError } from '../../lib/errors/ApiError'
import { apiErrorCode } from '../../lib/errors/apiErrorMessage'

export const SUBSCRIPTION_HISTORY_QUERY_KEY = ['subscription', 'history'] as const
export const SUBSCRIPTION_CURRENT_QUERY_KEY = ['subscription', 'current'] as const

/** Kanonisches Status-Set des Backends. Jeder Wert braucht einen `status.*`-i18n-Text. */
export const SUBSCRIPTION_HISTORY_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'expired',
  'other',
] as const

export type SubscriptionHistoryStatus = (typeof SUBSCRIPTION_HISTORY_STATUSES)[number]
export type SubscriptionHistorySource = 'stripe' | 'crypto' | 'code'

export type SubscriptionHistoryItem = {
  /** Quellenuebergreifend eindeutig, z. B. "stripe:42" — nur als React-Key/Testanker. */
  id: string
  /** Pay-DB-ID fuer den Cancel-Call. NUR bei source='stripe' gesetzt. */
  subscription_id: number | null
  source: SubscriptionHistorySource
  plan_id: string | null
  /** Roher Plan-/Produktname vom BE (z. B. "professional") — Anzeige baut das FE. */
  plan_label: string
  duration_months: number | null
  status: SubscriptionHistoryStatus | string
  /** Roher Provider-Status, nur Support/Debug (Tooltip). */
  raw_status?: string
  started_at: string | null
  /** null = "laeuft nicht aus" → Anzeige "Nie"/"Never" (v1-Paritaet). */
  expires_at: string | null
  current_period_end: string | null
  /** Volle "sub_..."-ID; wird gekuerzt angezeigt. */
  stripe_id: string | null
  cancel_at_period_end: boolean
  cancellable: boolean
}

export type SubscriptionHistoryMeta = {
  count: number
  has_payment_processor: boolean
  sources: string[]
}

/** Fehlercodes des Cancel-Calls, 1:1 auf i18n-Keys `history.cancel.errors.*` abbildbar. */
export type CancelErrorCode =
  | 'subscription_not_found'
  | 'subscription_not_cancelable'
  | 'cancel_failed'
  | 'payments_disabled'
  | 'unknown'

export class CancelSubscriptionError extends Error {
  constructor(public readonly code: CancelErrorCode) {
    super(`cancel subscription failed: ${code}`)
    this.name = 'CancelSubscriptionError'
  }
}

type HistoryResponse = {
  data?: SubscriptionHistoryItem[]
  meta?: Partial<SubscriptionHistoryMeta>
}

// /api/v2/subscriptions/* liegt (noch) nicht im installierten api-types-Paket —
// gezielte Casts nur an diesen beiden Aufrufstellen, kein globales `as any`
// (gleiches Muster wie crypto_quote in useSubscription.ts).
type UntypedGet = (url: string) => Promise<{ data?: unknown; error?: unknown; response?: Response }>
type UntypedDelete = (url: string) => Promise<{ data?: unknown; error?: unknown; response?: Response }>

const EMPTY_META: SubscriptionHistoryMeta = { count: 0, has_payment_processor: false, sources: [] }

export type UseSubscriptionHistoryResult = {
  items: SubscriptionHistoryItem[]
  meta: SubscriptionHistoryMeta
  isLoading: boolean
  isError: boolean
  refetch: () => void
  /** Kuendigt EIN Stripe-Abo zum Periodenende. Wirft CancelSubscriptionError. */
  cancelSubscription: (subscriptionId: number) => Promise<void>
  /** Pay-DB-ID des gerade laufenden Cancel-Calls (fuer Button-Loading). */
  cancellingId: number | null
}

function normalizeItems(raw: unknown): SubscriptionHistoryItem[] {
  const body = raw as HistoryResponse | undefined
  return Array.isArray(body?.data) ? body.data : []
}

function normalizeMeta(raw: unknown): SubscriptionHistoryMeta {
  const meta = (raw as HistoryResponse | undefined)?.meta
  if (!meta) return EMPTY_META
  return {
    count: typeof meta.count === 'number' ? meta.count : 0,
    has_payment_processor: meta.has_payment_processor === true,
    sources: Array.isArray(meta.sources) ? meta.sources : [],
  }
}

/**
 * ApiError → stabiler Fehlercode fuer die i18n-Auswahl.
 *
 * R4-Review F5: das BE liefert `{ error: { code, message } }` — `body.error` ist
 * also ein OBJEKT, kein String. Die frueher direkte String-Pruefung war damit
 * nie wahr, `subscription_not_cancelable` (422) landete im generischen
 * `cancel_failed`-Zweig. Der Code kommt jetzt aus `apiErrorCode` (versteht
 * `{ error: { code } }`, `{ code }` und die ApiError-Huelle).
 */
export function cancelErrorCodeFor(err: unknown): CancelErrorCode {
  if (err instanceof ApiError) {
    // `{ error: { code } }` (aktueller BE-Contract) via Helper, `{ error: 'code' }`
    // (aeltere Antworten) als Fallback — beides landet auf demselben Code.
    const legacy = (err.body as { error?: unknown } | undefined)?.error
    const code = apiErrorCode(err) ?? (typeof legacy === 'string' ? legacy : null)
    if (
      code === 'subscription_not_found' ||
      code === 'subscription_not_cancelable' ||
      code === 'cancel_failed' ||
      code === 'payments_disabled'
    ) {
      return code
    }
    if (err.status === 404) return 'subscription_not_found'
    if (err.status === 501) return 'payments_disabled'
    if (err.status === 422) return 'cancel_failed'
  }
  return 'unknown'
}

export function useSubscriptionHistory(): UseSubscriptionHistoryResult {
  const queryClient = useQueryClient()
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  const query = useQuery({
    queryKey: SUBSCRIPTION_HISTORY_QUERY_KEY,
    queryFn: async () => {
      const untypedGet = apiClient.GET as unknown as UntypedGet
      const result = await untypedGet('/api/v2/subscriptions/history')
      if (result.error) throw result.error
      return {
        items: normalizeItems(result.data),
        meta: normalizeMeta(result.data),
      }
    },
    retry: false,
  })

  const cancelSubscription = useCallback(
    async (subscriptionId: number) => {
      setCancellingId(subscriptionId)
      try {
        const untypedDelete = apiClient.DELETE as unknown as UntypedDelete
        const result = await untypedDelete(`/api/v2/subscriptions/${subscriptionId}`)
        if (result.error) throw result.error
        // BEIDE Keys invalidieren: sonst zeigt die Statuskarte oben weiterhin
        // "aktiv" waehrend die Historie bereits "Laeuft aus" meldet.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_HISTORY_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_CURRENT_QUERY_KEY }),
        ])
      } catch (err) {
        throw new CancelSubscriptionError(cancelErrorCodeFor(err))
      } finally {
        setCancellingId(null)
      }
    },
    [queryClient],
  )

  return {
    items: query.data?.items ?? [],
    meta: query.data?.meta ?? EMPTY_META,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
    cancelSubscription,
    cancellingId,
  }
}
